import {Updatable} from '@fantasmagames/core/src/update/Updatable';
import {Easing} from '@fantasmagames/core/src/tween/Easing';
import {Interpolation} from '@fantasmagames/core/src/tween/Interpolation';
import {TweenPath} from '@fantasmagames/core/src/tween/TweenPath';

export class Tween extends Updatable {
	protected _startTime: number = null;
	protected _duration: number = 1000;
	protected _currentTime: number;
	protected _isPlaying: boolean = false;
	private _repeat: number = 0;

	private _object: any;
	private _valuesStart: any = {};
	private _valuesEnd: any = {};
	private _valuesStartRepeat: any = {};
	private _yoyo: boolean = false;
	private _reversed: boolean = false;
	private _delayTime: number = 0;
	private _easingFunction: Function = Easing.Linear.None;
	private _interpolationFunction: Function = Interpolation.Linear;
	private _chainedTweens: Tween[] = [];
	private _onStartCallback: Function = null;
	private _onStartCalledCallback: Function = null;
	private _onStartCallbackFired: boolean = false;
	private _onUpdateCallback: Function = null;
	private _onRepeatCallback: Function = null;
	private _onCompleteCallback: Function = null;
	private _onStopCallback: Function = null;
	private _skipFirstUpdate: boolean;

	protected _path: TweenPath;
	protected _pathFrom: number;
	protected _pathTo: number;

	constructor(object?: any) {
		super();

		if (object) {
			this._object = object;
		}
	}

	public path(p: TweenPath, duration?: number): Tween {
		if (p !== undefined) {
			this._path = p;

			const distance = this._path.totalDistance();
			this._pathFrom = 0;
			this._pathTo = distance;
		}

		if (duration !== undefined) {
			this._duration = duration;
		}

		return this;
	}

	public to(properties: any, duration: number): Tween {
		if (duration !== undefined) {
			this._duration = duration;
		}

		this._valuesEnd = properties;

		this.parseStartValues(properties);

		return this;
	}

	public force(): void {
		for (const property in this._valuesEnd) {
			if (this._valuesEnd.hasOwnProperty(property)) {
				this._object[property] = this._valuesEnd[property];
			}
		}
	}

	public startAt(time?: number): Tween {
		super.start();

		this._onStartCalledCallback && this._onStartCalledCallback();

		this._onStartCallbackFired = false;

		this._startTime = time !== undefined ? time : Date.now();
		this._currentTime = this._startTime;
		this._startTime += this._delayTime;

		for (const property in this._valuesEnd) {
			if (this._valuesEnd.hasOwnProperty(property)) {
				// Check if an Array was provided as property value
				if (this._valuesEnd[property] instanceof Array) {
					if (this._valuesEnd[property].length === 0) {
						continue;
					}

					// Create a local copy of the Array with the start value at the front
					this._valuesEnd[property] = [this._object[property]].concat(this._valuesEnd[property]);
				}

				// If `to()` specifies a property that doesn't exist in the source object,
				// we should not set that property in the object
				if (this._valuesStart[property] === undefined) {
					continue;
				}

				this._valuesStart[property] = this._object[property];

				if (this._valuesStart[property] instanceof Array === false) {
					this._valuesStart[property] *= 1.0; // Ensures we're using numbers, not strings
				}

				this._valuesStartRepeat[property] = this._valuesStart[property] || 0;
			}
		}

		return this;
	}

	public start(): Tween {
		this.startAt();

		return this;
	}

	public stop(goToEnd: boolean = false): void {
		if (!this._isPlaying) {
			return;
		}

		super.stop();

		if (goToEnd) {
			this.applyEndProperties();
		}

		if (this._onStopCallback !== null) {
			this._onStopCallback.call(this._object);
		}

		this.stopChainedTweens();
	}

	protected applyEndProperties(): void {
		for (const property in this._valuesEnd) {
			// Don't update properties that do not exist in the source object
			if (this._valuesStart[property] === undefined) {
				continue;
			}

			let end = this._valuesEnd[property];

			if (end instanceof Array) {
				this._object[property] = end;
			} else {
				// Parses relative end values with start as base (e.g.: +10, -3)
				if (typeof end === 'string') {
					if (end.charAt(0) === '+' || end.charAt(0) === '-') {
						end = parseFloat(end);
					} else {
						end = parseFloat(end);
					}
				}

				// Protect against non numeric properties.
				if (typeof end === 'number') {
					this._object[property] = end;
				}
			}
		}
	}

	public stopChainedTweens(): void {
		for (let i = 0, numChainedTweens = this._chainedTweens.length; i < numChainedTweens; i++) {
			this._chainedTweens[i].stop();
		}
	}

	public delay(amount: number): Tween {
		this._delayTime = amount;

		return this;
	}

	public repeat(times: number): Tween {
		this._repeat = times;

		return this;
	}

	public getRepeat(): number {
		return this._repeat;
	}

	public yoyo(yoyo: boolean): Tween {
		this._yoyo = yoyo;

		return this;
	}

	public easing(easing: Function): Tween {
		this._easingFunction = easing;

		return this;
	}

	public interpolation(interpolation: Function): Tween {
		this._interpolationFunction = interpolation;

		return this;
	}

	public chain(chain: Tween): Tween;

	public chain(chain: Tween[]): Tween;

	public chain(chain: any): Tween {
		// if Tween[]
		if (chain.length) {
			this._chainedTweens = chain;
		} else {
			this._chainedTweens = [chain];
		}

		return this;
	}

	public onStart(callback: Function): Tween {
		this._onStartCallback = callback;

		return this;
	}

	public onStartCalled(callback: Function): Tween {
		this._onStartCalledCallback = callback;

		return this;
	}

	public onRepeat(callback: Function): Tween {
		this._onRepeatCallback = callback;

		return this;
	}

	public onUpdate(callback: Function): Tween {
		this._onUpdateCallback = callback;

		return this;
	}

	public onComplete(callback: Function): Tween {
		this._onCompleteCallback = callback;

		return this;
	}

	public onStop(callback: Function): Tween {
		this._onStopCallback = callback;

		return this;
	}

	public update(dt: number): void {
		if (this._skipFirstUpdate) {
			this._skipFirstUpdate = false;

			return;
		}

		this._currentTime += dt;

		const time = this._currentTime;
		let property;
		let elapsed;
		let value;

		if (time < this._startTime) {
			return;
		}

		if (!this._onStartCallbackFired) {
			if (this._onStartCallback !== null) {
				this._onStartCallback.call(this._object);
			}

			this._onStartCallbackFired = true;
		}

		elapsed = (time - this._startTime) / this._duration;
		elapsed = elapsed > 1 ? 1 : elapsed;

		value = this._easingFunction(elapsed);

		for (property in this._valuesEnd) {
			// Don't update properties that do not exist in the source object
			if (this._valuesStart[property] === undefined) {
				continue;
			}

			const start = this._valuesStart[property] || 0;
			let end = this._valuesEnd[property];

			if (end instanceof Array) {
				this._object[property] = this._interpolationFunction(end, value);
			} else {
				// Parses relative end values with start as base (e.g.: +10, -3)
				if (typeof end === 'string') {
					if (end.charAt(0) === '+' || end.charAt(0) === '-') {
						end = start + parseFloat(end);
					} else {
						end = parseFloat(end);
					}
				}

				// Protect against non numeric properties.
				if (typeof end === 'number') {
					this._object[property] = start + (end - start) * value;
				}
			}
		}

		this.applyPath(elapsed);

		if (this._onUpdateCallback !== null) {
			this._onUpdateCallback.call(this._object, value);
		}

		if (elapsed === 1) {
			if (this._repeat > 0) {
				if (isFinite(this._repeat)) {
					this._repeat--;
				}

				// Reassign starting values, restart by making startTime = now
				for (property in this._valuesStartRepeat) {
					if (this._valuesStartRepeat.hasOwnProperty(property)) {
						if (typeof this._valuesEnd[property] === 'string') {
							this._valuesStartRepeat[property] = this._valuesStartRepeat[property] + parseFloat(this._valuesEnd[property]);
						}

						if (this._yoyo) {
							const tmp = this._valuesStartRepeat[property];

							this._valuesStartRepeat[property] = this._valuesEnd[property];
							this._valuesEnd[property] = tmp;
						}

						this._valuesStart[property] = this._valuesStartRepeat[property];
					}
				}

				if (this._yoyo) {
					this._reversed = !this._reversed;
				}

				this._startTime = time + this._delayTime;
				this._currentTime = this._startTime;

				if (this._onRepeatCallback !== null) {
					this._onRepeatCallback.call(this.object, value);
				}

				if (this._repeat === 0 && this._onCompleteCallback !== null) {
					this._onCompleteCallback.call(this._object);
				}
			} else {
				if (this._onCompleteCallback !== null) {
					this._onCompleteCallback.call(this._object);
				}

				for (let i = 0, numChainedTweens = this._chainedTweens.length; i < numChainedTweens; i++) {
					this._updateManager.add(this._chainedTweens[i]);

					// Make the chained tweens start exactly at the time they should,
					// even if the `update()` method was called way past the duration of the tween
					this._chainedTweens[i]._skipFirstUpdate = true;
					this._chainedTweens[i].startAt(this._startTime + this._duration);
				}

				super.stop();
			}
			/*
			if (this.path) {
				_to = this._pathTo;
				_from = this._pathFrom;
				this._pathTo = _from;
				this._pathFrom = _to;
			}*/
		}
	}

	public getPredictedProperty(time: number, property: string): number {
		let elapsed = (this._currentTime - this._startTime + time) / this._duration;
		elapsed = elapsed > 1 ? 1 : elapsed;

		const value = this._easingFunction(elapsed);
		const start = this._valuesStart[property] || 0;
		let end = this._valuesEnd[property];

		if (end instanceof Array) {
			return this._interpolationFunction(end, value);
		} else {
			// Parses relative end values with start as base (e.g.: +10, -3)
			if (typeof end === 'string') {
				if (end.charAt(0) === '+' || end.charAt(0) === '-') {
					end = start + parseFloat(end);
				} else {
					end = parseFloat(end);
				}
			}

			// Protect against non numeric properties.
			if (typeof end === 'number') {
				return start + (end - start) * value;
			}
		}
	}

	protected applyPath(elapsedTime: number): void {
		if (this._path) {
			const time = this._duration;
			const b = this._pathFrom;
			const c = this._pathTo - this._pathFrom;
			const d = time;
			const t = time ? (elapsedTime * this._duration) / d : 1;
			const distance = b + c * this._easingFunction(t);
			const pos = this._path.getPointAtDistance(distance);
			this._object.position.set(pos.x, pos.y);
		}
	}

	private parseStartValues(properties: any): void {
		for (const field in properties) {
			if (properties.hasOwnProperty(field)) {
				try {
					this._valuesStart[field] = parseFloat(this.object[field]);
				} catch (e) {
					console.warn(e);
				}
			}
		}
	}

	public get chainedTweens(): Tween[] {
		return this._chainedTweens;
	}

	public get object(): any {
		return this._object;
	}

	public get duration(): number {
		return this._duration;
	}

	public get isPlaying(): boolean {
		return this._isPlaying;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/tween/Tween.ts